import { keyframes } from '@emotion/react';
import { ThemeConfig } from '../../theme';

/**
 * Design tokens for the public landing page.
 *
 * These are deliberately self-contained rather than an extension of the app's
 * `ThemeColors`. The five app presets (blue/dark/green/purple/orange) are a
 * *tenant white-label preference* — a property of a logged-in workspace. A
 * logged-out prospect visiting the marketing site must always see the Palbon
 * brand, not whatever preset happens to be sitting in their localStorage from
 * a previous session. In particular, the `dark` preset would invert the light
 * sections and destroy the dark -> light -> dark narrative the page is built on.
 *
 * So: the landing reads from here, never from `useAppTheme()`.
 */
export const landing = {
  // ---- Dark zone ----------------------------------------------------------
  ink: '#05060f',
  inkRaised: '#0a1024',
  inkDeep: '#0d1b3e',

  heroGradient: [
    'radial-gradient(1200px 600px at 50% -10%, #1e3a8a 0%, rgba(30,58,138,0) 60%)',
    'radial-gradient(900px 500px at 85% 20%, #4c1d95 0%, rgba(76,29,149,0) 55%)',
    'linear-gradient(180deg, #05060f 0%, #0a1024 45%, #0d1b3e 100%)',
  ].join(', '),

  accent: '#38bdf8',
  accentMid: '#818cf8',
  accentAlt: '#a78bfa',
  accentGradient: 'linear-gradient(90deg, #38bdf8 0%, #818cf8 50%, #a78bfa 100%)',

  glassBg: 'rgba(255,255,255,0.05)',
  glassBorder: '1px solid rgba(255,255,255,0.12)',
  glassBlur: 'blur(12px)',
  /**
   * Kept deliberately light. A heavy black shadow on an already-dark band
   * doesn't read as elevation — there's nothing for it to darken, so it just
   * muddies the surface underneath. On dark, separation comes from the
   * hairline border and the lit-surface gradient, not from drop shadow.
   */
  glassShadow: '0 2px 12px rgba(0,0,0,0.16)',

  textOnDark: '#f8fafc',
  textOnDarkMuted: 'rgba(226,232,240,0.72)',
  gridLine: 'rgba(255,255,255,0.04)',

  // ---- Light zone ---------------------------------------------------------
  paper: '#ffffff',
  paperAlt: '#f6f8fc',
  textOnLight: '#0f172a',
  textOnLightMuted: '#475569',
  borderLight: '#e2e8f0',

  /**
   * MANDATORY for accent-coloured text on light backgrounds.
   * `accent` (#38bdf8) on white is ~1.9:1 and fails WCAG AA outright.
   * #0284c7 is ~4.6:1. The bright accent may only appear on light surfaces as
   * a gradient *fill* — borders, bars, icon chips — never as text.
   */
  accentOnLight: '#0284c7',

  // ---- Rhythm -------------------------------------------------------------
  sectionPy: { xs: 8, md: 14 },
  cardRadius: 16,
} as const;

/**
 * Fluid type scale. `clamp()` avoids per-breakpoint overrides entirely.
 */
export const landingType = {
  h1: {
    fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
    fontWeight: 700,
    letterSpacing: '-0.03em',
    lineHeight: 1.05,
  },
  h2: {
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
  },
  h3: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.35,
  },
  eyebrow: {
    // Mono for eyebrows only. A separate typeface for the smallest, most
    // repeated label is what stops a page of one font reading as a template.
    fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
  },
  lead: {
    fontSize: 'clamp(1.0625rem, 1.5vw, 1.25rem)',
    lineHeight: 1.7,
  },
  body: {
    fontSize: '1rem',
    lineHeight: 1.65,
  },
} as const;

// ---- Keyframes ------------------------------------------------------------
// Interpolated into MUI `sx`, which compiles through emotion.

export const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%      { transform: translateY(-20px); }
`;

export const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(56,189,248,0.22); }
  50%      { box-shadow: 0 0 20px 3px rgba(56,189,248,0.12); }
`;

export const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/** Slow drift across the accent gradient, so gradient text is quietly alive. */
export const drift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
`;

/** A line climbing out of its own overflow box. Used for the hero headline. */
export const lineIn = keyframes`
  from { transform: translateY(112%); }
  to   { transform: translateY(0); }
`;

/**
 * Spread into any `sx` that runs an infinite animation.
 * Infinite motion is the specific class that triggers vestibular symptoms —
 * this is not optional.
 */
export const reducedMotion = {
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    transition: 'none',
  },
};

/** Visible focus ring for interactive elements sitting on the ink background. */
export const focusRingOnDark = {
  '&:focus-visible': {
    outline: `2px solid ${landing.accent}`,
    outlineOffset: '2px',
  },
};

/**
 * Glass surface used by cards, the nav and the stats strip.
 *
 * The fill is a vertical white-alpha *gradient*, not a flat tint — it reads as
 * a lit surface rather than a translucent rectangle, which is most of the
 * difference between a glass card that looks designed and one that looks cheap.
 */
export const glassSurface = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.02))',
  border: landing.glassBorder,
  backdropFilter: landing.glassBlur,
  WebkitBackdropFilter: landing.glassBlur,
  boxShadow: landing.glassShadow,
  borderRadius: `${landing.cardRadius}px`,
};

/** Text filled with the brand gradient. Headline-size only — never body copy. */
export const gradientText = {
  background: landing.accentGradient,
  backgroundSize: '260% 100%',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  color: 'transparent',
  animation: `${drift} 9s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

/**
 * Masked line-reveal: the child climbs out of an overflow-hidden parent.
 * Reads far better than a fade on large display type.
 *
 * Fails open — under reduced motion the child sits at its final position
 * rather than staying translated out of view.
 */
export const maskedLine = (delay: string) => ({
  display: 'block',
  overflow: 'hidden',
  paddingBottom: '0.08em',
  '& > span': {
    display: 'block',
    transform: 'translateY(112%)',
    animation: `${lineIn} .95s cubic-bezier(.16,1,.3,1) forwards`,
    animationDelay: delay,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& > span': { animation: 'none', transform: 'none' },
  },
});

/**
 * A fixed, blue-derived ThemeConfig used to scope MUI components on the
 * landing page, so Button/Chip/Accordion/TextField render on-brand no matter
 * which preset the visitor has stored.
 */
export const palbonLandingConfig: ThemeConfig = {
  id: 'palbon-landing',
  name: 'Palbon Landing',
  brand: {
    name: 'Palbon ERP',
    shortName: 'Palbon',
    tagline: 'AI-enabled ERP for growing businesses',
    logo: '/logo192.png',
    version: '1.0.0',
  },
  colors: {
    primary: '#0284c7',
    primaryDark: '#0369a1',
    primaryLight: '#38bdf8',

    secondary: '#7c3aed',
    secondaryDark: '#6d28d9',
    secondaryLight: '#a78bfa',

    sidebarBg: landing.ink,
    sidebarBgGradient: landing.heroGradient,
    sidebarText: landing.textOnDark,
    sidebarTextMuted: landing.textOnDarkMuted,
    sidebarActiveItem: 'rgba(255,255,255,0.15)',
    sidebarHoverItem: 'rgba(255,255,255,0.08)',
    sidebarAccent: landing.accent,
    sidebarAccentGradient: `linear-gradient(180deg, ${landing.accent} 0%, ${landing.accentMid} 50%, ${landing.accentAlt} 100%)`,

    headerBg: landing.ink,
    headerBgGradient: landing.heroGradient,
    headerText: landing.textOnDark,

    background: landing.paperAlt,
    backgroundPaper: landing.paper,
    backgroundAlt: '#eff6ff',

    textPrimary: landing.textOnLight,
    textSecondary: landing.textOnLightMuted,

    success: '#16a34a',
    warning: '#f59e0b',
    error: '#dc2626',
    info: '#0284c7',

    divider: landing.borderLight,
    border: landing.borderLight,
  },
};
